import React, { useState, useMemo, useEffect, useRef } from "react";
import { compareSchoolYearDesc } from "../../../../../util/schoolYear";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../../../context/UIContext";
import DropdownSchoolYear from "../../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import { filterSessionsForSettingsScope } from "../../../../../util/billing/normalizeBillingEntities";
import styles from "../../css/BillingManagement.module.css";

function tempSessionId() {
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/** 스코프 내 실제 세션만, 화면 순서대로 display_order 부여 */
function buildPersistedReorderOrders(scopedSessions) {
  let pos = 0;
  return scopedSessions
    .filter((x) => !String(x.id).startsWith("temp_"))
    .map((x) => ({ id: x.id, display_order: ++pos }));
}

export default function BillingSessionManager({
  schoolId,
  sessions,
  setSessions,
  settingData,
  schoolYearId,
  initialSchoolYear,
  selectedSessionId,
  onSelectSession,
  handleToastCenterTop,
  billingApi,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const { t } = useTranslation();
  const { setToastConfirmModalState } = useUIContext();

  const [localSchoolYear, setLocalSchoolYear] = useState(
    initialSchoolYear || "",
  );
  const [localSchoolYearId, setLocalSchoolYearId] = useState(
    schoolYearId || "",
  );
  const [selectedCourse, setSelectedCourse] = useState("");
  const [confirmDelete, setConfirmDelete] = useState([]);
  const dragIdx = useRef(null);

  const {
    createSessionMutation,
    patchSessionMutation,
    deleteSessionMutation,
    reorderSessionsMutation,
  } = billingApi;

  const courseOptions = useMemo(() => {
    if (!settingData || !localSchoolYear) return [];
    const yearEntry = settingData.find(
      (s) =>
        s.hierarchy_level === 0 &&
        String(s.school_year) === String(localSchoolYear),
    );
    if (!yearEntry) return [];
    return settingData.filter(
      (s) => s.hierarchy_level === 1 && s.parent_id === yearEntry.id,
    );
  }, [settingData, localSchoolYear]);

  useEffect(() => {
    if (!localSchoolYearId && settingData && settingData.length > 0) {
      const sorted = [...settingData]
        .filter((s) => s.hierarchy_level === 0 && s.parent_id === 0)
        .sort((a, b) => compareSchoolYearDesc(a.school_year, b.school_year));
      if (sorted.length > 0) {
        setLocalSchoolYear(sorted[0].school_year);
        setLocalSchoolYearId(String(sorted[0].id));
      }
    }
  }, [settingData, localSchoolYearId]);

  useEffect(() => {
    if (schoolYearId) setLocalSchoolYearId(schoolYearId);
  }, [schoolYearId]);

  const handleLocalDropdownChange = (e) => {
    const { name, value } = e.target;
    const text = e.target.selectedOptions[0].text;
    if (name === "0") {
      setLocalSchoolYear(text);
      setLocalSchoolYearId(value);
      setSelectedCourse("");
      onSelectSession(null);
    }
  };

  const filteredSessions = useMemo(
    () =>
      filterSessionsForSettingsScope(
        sessions,
        localSchoolYearId,
        selectedCourse,
      ),
    [sessions, localSchoolYearId, selectedCourse],
  );

  const hierarchyOneForScope = () =>
    selectedCourse === "__INTEGRATED__" ? "" : String(selectedCourse);

  const normalizeSessionName = (name) => String(name ?? "").trim();

  /** 같은 school_year_id + hierarchy_one 안에서 기간 이름(session_name) 유일 */
  const isDuplicateSessionName = (sessionRow, excludeId) => {
    const name = normalizeSessionName(sessionRow.session_name);
    if (!name) return false;
    const yid = Number(sessionRow.school_year_id);
    const h = String(sessionRow.hierarchy_one ?? "");
    return sessions.some(
      (x) =>
        x.id !== excludeId &&
        Number(x.school_year_id) === yid &&
        String(x.hierarchy_one ?? "") === h &&
        normalizeSessionName(x.session_name) === name,
    );
  };

  const handleFieldChange = (id, field, value) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const handleAdd = () => {
    if (!canCreate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noAdd") || "추가 권한이 없습니다.",
      );
      return;
    }
    if (!localSchoolYearId || !selectedCourse) {
      handleToastCenterTop(
        t("billingManagement.messages.sessionSelectSchoolYearCourse") ||
          "학년도와 과정을 먼저 선택해주세요.",
      );
      return;
    }
    const id = tempSessionId();
    setSessions((prev) => [
      ...prev,
      {
        id,
        school_year_id: Number(localSchoolYearId),
        hierarchy_one: hierarchyOneForScope(),
        session_name: "",
        display_order: filteredSessions.length + 1,
        issue_date: "",
        cms_message: "",
        unreg_message: "",
      },
    ]);
    onSelectSession(id);
  };

  const handleConfirmDelete = (id) => setConfirmDelete((p) => [...p, id]);
  const handleCancelDelete = () => setConfirmDelete([]);

  const handleDelete = (id) => {
    if (!canDelete) {
      handleToastCenterTop(
        t("billingManagement.permissions.noDelete") || "삭제 권한이 없습니다.",
      );
      return;
    }
    if (String(id).startsWith("temp_")) {
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (selectedSessionId === id) onSelectSession(null);
      setConfirmDelete([]);
      return;
    }
    setToastConfirmModalState({
      visible: true,
      message:
        t("billingManagement.messages.sessionDeleteConfirm") ||
        "해당 기간을 삭제하면 여기에 속한 항목과 학생들 기록도 모두 삭제 됩니다. 정말 삭제하시겠습니까?",
      onConfirm: () => {
        deleteSessionMutation.mutate(id, {
          onSuccess: () => {
            if (selectedSessionId === id) onSelectSession(null);
            setConfirmDelete([]);
            handleToastCenterTop(
              t("billingManagement.messages.sessionDeleted") ||
                "기간이 삭제되었습니다.",
            );
          },
          onError: (err) =>
            handleToastCenterTop(
              err?.response?.data?.message ||
                err?.message ||
                t("billingManagement.errors.deleteFailed") || "삭제 실패",
            ),
        });
      },
      onCancel: () => {},
    });
  };

  const handleSaveRow = (s) => {
    const isTempRow = String(s.id).startsWith("temp_");
    if (isTempRow && !canCreate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noSave") || "저장 권한이 없습니다.",
      );
      return;
    }
    if (!isTempRow && !canUpdate) {
      handleToastCenterTop(
        t("billingManagement.permissions.noUpdate") || "수정 권한이 없습니다.",
      );
      return;
    }
    const sessionName = normalizeSessionName(s.session_name);
    if (!sessionName) {
      handleToastCenterTop(
        t("billingManagement.messages.sessionNameRequired") ||
          "기간 이름을 입력해주세요.",
      );
      return;
    }
    if (isDuplicateSessionName({ ...s, session_name: sessionName }, s.id)) {
      handleToastCenterTop(
        t("billingManagement.messages.sessionDuplicateName") ||
          "같은 학년도·과정에서 동일한 기간 이름은 사용할 수 없습니다.",
      );
      return;
    }

    const isTemp = isTempRow;

    const runReorderForScope = (sessionsSnapshot) => {
      const list = filterSessionsForSettingsScope(
        sessionsSnapshot,
        localSchoolYearId,
        selectedCourse,
      );
      const orders = buildPersistedReorderOrders(list);
      if (orders.length === 0) return;
      reorderSessionsMutation.mutate(
        {
          school_year_id: Number(localSchoolYearId),
          hierarchy_one: hierarchyOneForScope(),
          orders,
        },
        {
          onError: (err) =>
            handleToastCenterTop(
              err?.response?.data?.message ||
                err?.message ||
                t("billingManagement.messages.reorderFailed") ||
                "순서 저장 실패",
            ),
        },
      );
    };

    if (isTemp) {
      createSessionMutation.mutate(
        {
          schoolid: schoolId,
          school_year_id: Number(localSchoolYearId),
          session_name: sessionName,
          hierarchy_one: hierarchyOneForScope(),
          display_order: s.display_order,
          issue_date: s.issue_date ?? "",
          cms_message: s.cms_message ?? "",
          unreg_message: s.unreg_message ?? "",
        },
        {
          onSuccess: (data) => {
            const newId = data?.id ?? data?.session?.id;
            setSessions((prev) => {
              const next = prev.map((row) =>
                row.id === s.id
                  ? {
                      ...row,
                      ...(newId ? { id: newId } : {}),
                      session_name: sessionName,
                    }
                  : row,
              );
              queueMicrotask(() => runReorderForScope(next));
              return next;
            });
            if (newId && selectedSessionId === s.id) onSelectSession(newId);
            handleToastCenterTop(
              t("billingManagement.messages.saved") || "저장되었습니다.",
            );
          },
          onError: (err) =>
            handleToastCenterTop(
              err?.response?.data?.message ||
                err?.message ||
                t("billingManagement.errors.saveFailed") ||
                "저장 실패",
            ),
        },
      );
      return;
    }

    patchSessionMutation.mutate(
      {
        id: s.id,
        body: {
          session_name: sessionName,
          display_order: s.display_order,
          issue_date: s.issue_date,
          cms_message: s.cms_message,
          unreg_message: s.unreg_message,
          hierarchy_one: s.hierarchy_one,
          school_year_id: s.school_year_id,
        },
      },
      {
        onSuccess: () => {
          handleToastCenterTop(
            t("billingManagement.messages.saved") || "저장되었습니다.",
          );
          setSessions((prev) => {
            const next = prev.map((row) =>
              row.id === s.id ? { ...row, session_name: sessionName } : row,
            );
            queueMicrotask(() => runReorderForScope(next));
            return next;
          });
        },
        onError: (err) =>
          handleToastCenterTop(
            err?.response?.data?.message ||
              err?.message ||
              t("billingManagement.errors.saveFailed") ||
              "저장 실패",
          ),
      },
    );
  };

  const handleDragStart = (idx) => {
    if (!canUpdate) return;
    dragIdx.current = idx;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (!canUpdate) return;
    const from = dragIdx.current;
    if (from === null || from === dropIndex || !selectedCourse) return;

    const reordered = [...filteredSessions];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);

    const orders = reordered.map((s, i) => ({
      id: s.id,
      display_order: i + 1,
    }));

    setSessions((prev) =>
      prev.map((s) => {
        const o = orders.find((x) => x.id === s.id);
        return o ? { ...s, display_order: o.display_order } : s;
      }),
    );

    dragIdx.current = null;
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h6 className="mb-0">
          {t("billingManagement.labels.sessionSettings") || "기간 설정"}
        </h6>
        <div style={{ width: 95 }}>
          <DropdownSchoolYear
            settingData={settingData}
            handleDropdownChange={handleLocalDropdownChange}
            schoolYearId={localSchoolYearId}
            allYears={false}
          />
        </div>
        <select
          className="form-select form-select-sm"
          style={{ width: 110 }}
          value={selectedCourse}
          onChange={(e) => {
            setSelectedCourse(e.target.value);
            onSelectSession(null);
          }}
        >
          <option value="">
            {t("billingManagement.labels.selectPlaceholder") || "선택"}
          </option>
          <option value="__INTEGRATED__">
            {t("billingManagement.labels.integrated") || "통합"}
          </option>
          {courseOptions.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.group_name}
            </option>
          ))}
        </select>
        {selectedCourse && (
          <button
            className="btn btn-outline-primary btn-sm ms-auto"
            onClick={handleAdd}
            disabled={!canCreate}
          >
            {t("common.labels.add") || "추가"}
          </button>
        )}
      </div>

      {selectedCourse && (
        <table
          className="table table-bordered table-sm mb-0"
          style={{ fontSize: "13px" }}
        >
          <thead className="table-light">
            <tr>
              <th
                style={{ width: 40 }}
                title={
                  t("billingManagement.labels.dragOrderTitle") ||
                  "번호 칸을 드래그하면 순서 변경"
                }
              >
                {t("common.labels.order") || "순서"}
              </th>
              <th style={{ width: 80 }}>
                {t("billingManagement.labels.sessionColumnPeriod") || "기간"}
              </th>
              <th style={{ width: 100 }}>
                {t("billingManagement.labels.issueDate") || "발행일"}
              </th>
              <th>
                {t("billingManagement.labels.cmsColumn") || "CMS"}
              </th>
              <th>
                {t("billingManagement.labels.unregColumn") || "미등록"}
              </th>
              <th style={{ width: 55 }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  {t("billingManagement.labels.noSessions") ||
                    "기간이 없습니다."}
                </td>
              </tr>
            ) : (
              filteredSessions.map((s, idx) => {
                const rowIsTemp = String(s.id).startsWith("temp_");
                const canEditRow = rowIsTemp ? canCreate : canUpdate;
                return (
                <tr
                  key={s.id}
                  className={
                    selectedSessionId === s.id
                      ? styles.billingSessionRowSelected
                      : undefined
                  }
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  onFocusCapture={() => onSelectSession(s.id)}
                  style={{
                    borderLeft:
                      selectedSessionId === s.id
                        ? undefined
                        : "3px solid transparent",
                  }}
                  onClick={() => onSelectSession(s.id)}
                >
                  <td
                    className="text-center align-middle text-muted"
                    draggable={canUpdate}
                    title={
                      t("billingManagement.labels.dragHandleTitle") ||
                      "이 칸을 드래그하여 순서 변경"
                    }
                    onDragStart={() => handleDragStart(idx)}
                    style={{
                      cursor: canUpdate ? "grab" : "default",
                      userSelect: "none",
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={s.session_name}
                      disabled={!canEditRow}
                      onChange={(e) =>
                        handleFieldChange(s.id, "session_name", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={s.issue_date ?? ""}
                      disabled={!canEditRow}
                      onChange={(e) =>
                        handleFieldChange(s.id, "issue_date", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      rows={3}
                      value={s.cms_message || ""}
                      disabled={!canEditRow}
                      onChange={(e) =>
                        handleFieldChange(s.id, "cms_message", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      className="form-control form-control-sm"
                      style={{ fontSize: "13px" }}
                      rows={3}
                      value={s.unreg_message || ""}
                      disabled={!canEditRow}
                      onChange={(e) =>
                        handleFieldChange(s.id, "unreg_message", e.target.value)
                      }
                    />
                  </td>
                  <td className="text-center align-middle">
                    <button
                      className="btn btn-sm btn-primary m-0 px-2"
                      style={{ fontSize: "11px" }}
                      onClick={() => handleSaveRow(s)}
                      disabled={
                        !canEditRow ||
                        patchSessionMutation.isLoading ||
                        createSessionMutation.isLoading
                      }
                    >
                      {t("billingManagement.labels.save") || "저장"}
                    </button>
                    {confirmDelete.includes(s.id) ? (
                      <>
                        <button
                          className="btn btn-sm btn-danger m-0 mt-1 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => handleDelete(s.id)}
                          disabled={!canDelete}
                        >
                          {t("common.labels.confirm") || "확인"}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary m-0 mt-1 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => handleCancelDelete()}
                        >
                          {t("common.labels.cancel") || "취소"}
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-sm btn-warning m-0 mt-1 px-2"
                        style={{ fontSize: "11px" }}
                        onClick={() => handleConfirmDelete(s.id)}
                        disabled={!canDelete}
                      >
                        {t("common.labels.delete") || "삭제"}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
